import { buildGoogleAdVariants } from '../../../lib/buildGoogleAdVariants';
import type { GoogleHeadline } from '../../../types/campaign';
import type { PublicPreviewDto } from '../publicDto';

// Ad Preview Mockups Stage 1 §4.6 + §13.7.
//
// Renders 3-5 Google Search ad mockups. Pin-aware: headlines pinned to
// position N appear ONLY in slot N; unpinned headlines fill any slot
// that has no pinned candidates. Helper buildGoogleAdVariants owns
// the rotation logic + caps; this component is presentation only.
//
// Each rendered card shows up to three headlines joined by " · " (the
// closest neutral separator to Google's actual " | " — using a real
// pipe inside a sentence felt jarring at the mockup's small type
// size). Empty slots are omitted rather than rendered as "Title — —".

type Props = {
  google: NonNullable<PublicPreviewDto['google']>;
  brandName: string;
};

function deriveDisplayUrl(finalUrl: string | null, brandName: string): string {
  if (finalUrl) {
    try {
      const u = new URL(finalUrl);
      const host = u.hostname.replace(/^www\./, '');
      const path = u.pathname && u.pathname !== '/' ? u.pathname : '';
      return host + path;
    } catch {
      // fall through
    }
  }
  return `${brandName.toLowerCase().replace(/\s+/g, '')}.example`;
}

export function GoogleSearchMockup({ google, brandName }: Props) {
  // The public DTO carries `headlines` as `Array<{ text, pinPosition }>` —
  // pass through directly to the helper which understands the shape.
  const headlines: GoogleHeadline[] = google.headlines;
  const variants = buildGoogleAdVariants(headlines, google.descriptions);
  const displayUrl = deriveDisplayUrl(google.finalUrl, brandName);

  if (variants.length === 0) {
    return (
      <p className="text-sm text-slate-500 italic py-8 text-center">
        No Google headlines or descriptions to preview.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {variants.map((v, i) => (
        <SearchAdCard
          key={i}
          displayUrl={displayUrl}
          headlineSlots={v.headlines}
          description={v.description}
          variantNumber={i + 1}
          totalVariants={variants.length}
        />
      ))}
      <p className="text-xs text-slate-400 text-center pt-2">
        Google rotates headlines and descriptions automatically — actual
        ads may combine these differently. Pinned positions are
        respected.
      </p>
    </div>
  );
}

function SearchAdCard({
  displayUrl,
  headlineSlots,
  description,
  variantNumber,
  totalVariants,
}: {
  displayUrl: string;
  headlineSlots: [string, string | null, string | null];
  description: string;
  variantNumber: number;
  totalVariants: number;
}) {
  // Drop null slots before joining so the rendered headline reads
  // "H1 · H2" instead of "H1 · ".
  const titleLine = headlineSlots
    .filter((h): h is string => h !== null && h.length > 0)
    .join(' · ');
  return (
    <article className="mx-auto max-w-[600px] w-full rounded-md border border-slate-200 bg-white p-4 space-y-1">
      <div className="flex items-center gap-2 text-xs text-slate-600">
        <span
          className="inline-flex items-center justify-center rounded px-1.5 py-0.5 bg-slate-100 text-slate-800 font-semibold text-[10px] uppercase"
          aria-label="Sponsored result"
        >
          Ad
        </span>
        <span className="truncate">{displayUrl}</span>
      </div>
      <h3 className="text-blue-700 hover:underline cursor-default text-base leading-snug">
        {titleLine}
      </h3>
      <p className="text-sm text-slate-700 leading-snug">{description}</p>
      <p className="text-[10px] text-slate-400 pt-1">
        Variant {variantNumber} of {totalVariants}
      </p>
    </article>
  );
}
